import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { usePWH508Queue, ItemState } from './hooks/usePWH508Queue';
import { usePWH508Approval } from './hooks/usePWH508Approval';
import { usePWH508Permission } from './hooks/usePWH508Permission';
import { pwh508Api } from '../../services/pwh508Api';
import { BatchAcceptanceBridge, pwh508f1AcceptanceManifest } from '../../features/acceptance';
import './PWH508Form.css';

// ---- Component ----
// PWH508F1 「請款單核准-主管」— a batch approval queue (no single-record
// CRUD/navigation), same shape as the already-shipped PWH5M8 (reversal) and
// D504 (subcontract receipt approval) modules. Follows that established
// pattern rather than SharedActionBar, which is built around single-record
// navigate/add/edit/delete + submit/approve/reject lifecycles that don't
// exist here (see delivery report for the full rationale).
const PWH508Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    queried,
    selectedCount,
    allSelected,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    resetItems,
  } = usePWH508Queue();

  const { executing, handleExecute } = usePWH508Approval(items, resetItems);
  const { canApprove } = usePWH508Permission();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // BR-F16 (Text1.Valid): `THISFORM.TEXT2.VALUE = SUBSTR(TRIM(THIS.VALUE)+"zzzzzz",1,6)`
  // — leaving the start-no field auto-fills the end-no field with the same
  // prefix padded to 6 chars with "z" (i.e. "biggest doc no with this prefix").
  const applyEndNoAutofill = useCallback((value: string) => {
    const filled = (value.trim() + 'zzzzzz').slice(0, 6);
    setEndNo(filled);
    return filled;
  }, []);

  const onStartNoBlur = useCallback(() => {
    applyEndNoAutofill(startNo);
  }, [applyEndNoAutofill, startNo]);

  // FoxPro fires Text1.Valid (blur) before the default button's Click when
  // Enter triggers the query, so replicate the same order here — compute the
  // filled value synchronously rather than relying on the (stale) endNo state.
  const onStartNoKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        const filled = applyEndNoAutofill(startNo);
        handleQuery(startNo, filled);
      }
    },
    [applyEndNoAutofill, startNo, handleQuery]
  );

  // Pwtcmddoc1 ("說明"): legacy opens PWU016F6, a help-document viewer whose
  // content was never extracted into contexts/foxpro-truth-v1 and has no Web
  // ERP backend. Rather than inventing placeholder help text, disclose the
  // gap honestly (see fidelity-audit report's 待業主裁決 list).
  const onShowHelp = useCallback(() => {
    Modal.info({
      title: '說明',
      content:
        '原始 FoxPro 版本此處為「說明」按鈕，開啟 PWU016F6 說明文件檢視器。Web 版尚未建置對應的說明文件內容（待後續裁決）。',
    });
  }, []);

  // BR-V02: confirm dialog text is copied verbatim from
  // pwh508f1_procedures.md (PWU016F2 call params L_MSG1/L_MSG2) — not
  // reworded.
  const onExecute = useCallback(() => {
    if (selectedCount === 0) return;
    Modal.confirm({
      title: '核準確認!',
      content: `請確認核准本資料! (已勾選 ${selectedCount} 筆)`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ui_spec.json Grid1.columns order: 核准/請款單/待核等級/請款類別/公司員工/請款日期
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          disabled={items.length === 0 || !canApprove}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          disabled={!canApprove}
          onChange={(e) => handleToggleItem(record.item.fh110_01, e.target.checked)}
        />
      ),
    },
    {
      title: '請款單',
      dataIndex: ['item', 'fh110_01'],
      key: 'fh110_01',
      width: 90,
      sorter: (a, b) => a.item.fh110_01.localeCompare(b.item.fh110_01),
      onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
    },
    {
      title: '待核等級',
      dataIndex: ['item', 'fh110_802'],
      key: 'fh110_802',
      width: 120,
      onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
    },
    {
      title: '請款類別',
      dataIndex: ['item', 'fh110_221'],
      key: 'fh110_221',
      width: 150,
      sorter: (a, b) => a.item.fh110_221.localeCompare(b.item.fh110_221),
      onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
    },
    {
      title: '公司/員工',
      dataIndex: ['item', 'fh110_16'],
      key: 'fh110_16',
      width: 200,
      onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
    },
    {
      title: '請款日期',
      dataIndex: ['item', 'fh110_11'],
      key: 'fh110_11',
      width: 110,
      onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
    },
  ];

  const approveBtn = (
    <Button
      type="primary"
      size="large"
      disabled={selectedCount === 0 || executing || !canApprove}
      loading={executing}
      onClick={onExecute}
    >
      存檔 ({selectedCount})
    </Button>
  );

  return (
    <PermissionGuard module="pwh508f1" action="query">
      <div className="pwh508-form">
        {(loading || executing) && (
          <div className="loading-overlay">{executing ? '核准執行中...' : '載入中...'}</div>
        )}

        <div className="page-wrapper">
          {/* Page Title Bar */}
          <div className="page-title-bar">
            <div className="page-title-left">
              <nav className="breadcrumb-inline">
                <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
                <span>/</span>
                <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>請款管理</a>
                <span>/</span>
                <span className="current">請款單核准-主管</span>
              </nav>
              <span className="title-separator">&mdash;</span>
              <h1>請款單核准-主管</h1>
              <span className="badge">PWH508</span>
            </div>
          </div>

          {/* Query Bar — Text1/Text2 「請款單.從:」/「至」 + Check1「待我審核的單據」+ Command1「確定範圍」 */}
          <div className="query-bar">
            <label data-contract-field="fh110_01">請款單.從:</label>
            <Input
              value={startNo}
              onChange={(e) => setStartNo(e.target.value)}
              onKeyDown={onStartNoKeyDown}
              onBlur={onStartNoBlur}
              placeholder="起始單號"
              style={{ width: 160 }}
              allowClear
            />
            <span className="separator">至</span>
            <Input
              value={endNo}
              onChange={(e) => setEndNo(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="結束單號"
              style={{ width: 160 }}
              allowClear
            />
            {/* form-level Check1「待我審核的單據」(BR-F20): legacy filters the
                queue to rows whose target level equals the caller's own
                approval level. The queue endpoint (GET /api/pwh508/queue)
                takes no such filter and never returns the caller's approval
                level, so this can't be wired without a backend/contract
                change — kept visible (layout fidelity) but honestly
                disabled instead of faking a no-op filter. */}
            <Tooltip title="此篩選需要後端查詢條件與核准層級支援，尚未提供（待後續裁決）">
              <Checkbox disabled>待我審核的單據</Checkbox>
            </Tooltip>
            <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWH508-ACT-BUILD-QUEUE">
              確定範圍
            </Button>
          </div>

          {/* Table Card — Grid1 */}
          <div className="table-card">
            {queried && (
              <div className="table-header">
                <div className="info">
                  共 <strong>{items.length}</strong> 筆
                  {selectedCount > 0 && (
                    <span>，已勾選 <strong>{selectedCount}</strong> 筆</span>
                  )}
                </div>
              </div>
            )}

            {queried ? (
              <Table<ItemState>
                columns={columns}
                dataSource={items}
                rowKey={(record) => record.item.fh110_01}
                pagination={false}
                size="small"
                scroll={{ y: 420 }}
                locale={{ emptyText: '查無資料' }}
              />
            ) : (
              <div className="empty-state">請輸入請款單號範圍並點擊「確定範圍」查詢</div>
            )}
          </div>

          {/* Bottom Action Bar — Pwtcmddoc1「說明」/ CMD1「存檔」/ CMDEXIT「離開」 */}
          <div className="action-bar">
            <Button size="large" onClick={onShowHelp}>
              說明
            </Button>
            {canApprove ? approveBtn : <Tooltip title="權限不足">{approveBtn}</Tooltip>}
            <Button danger size="large" onClick={() => navigate('/erp')}>
              離開
            </Button>
          </div>
          <BatchAcceptanceBridge
            manifest={pwh508f1AcceptanceManifest}
            records={items.map(state => state.item)}
            selectedKeys={items.filter(state => state.selected).map(state => state.item.fh110_01)}
            keyOf={(item) => item.fh110_01}
            queryExact={async (key) => (await pwh508Api.queryQueue(key, key)).items?.[0] || null}
            lastApiResponse={items}
            loading={loading || executing}
            onAutoFill={() => {
              const first = items[0];
              if (first) handleToggleItem(first.item.fh110_01, true);
            }}
            autoFillDisabled={items.length === 0}
            autoFillReason="只選取正式 API 回傳的第一筆應付請款核准候選。"
            screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
          />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default PWH508Form;
